import { useCallback, useMemo, useState } from 'react';
import { useLiveRxQuery } from 'rxdb/plugins/react';
import { useStableQuery } from '../../db/useStableQuery';
import type { ProjectDocType } from '@ast-data/shared';
import { useAbility } from '../auth/abilityContext';
import { ProjectLabel } from '../projects/ProjectLabel';
import { projectLabelText } from '../projects/projectLabelText';
import { projectTitleParts } from '../projects/projectTitle';
import { hasAnyFieldCategory } from './fieldCategories';
import FieldCardList, { type FieldCardRow } from './FieldCardList';

interface FieldProjectPickerProps {
    /** Should be stable (`useCallback`) so the cards can skip re-rendering. */
    onPick: (projectId: string) => void;
    /** Highlighted as the current one when opened from the project header. */
    activeProjectId?: string;
}

/**
 * Touch-first project picker: the field tool's entry point, and the body of the
 * fullscreen dialog behind the project header. Lists only projects the user can
 * actually record something in.
 *
 * Renders a FieldCardList, so whatever hosts it has to be a flex column with a
 * definite height.
 */
export default function FieldProjectPicker({
    onPick,
    activeProjectId
}: FieldProjectPickerProps) {
    const ability = useAbility();
    const [search, setSearch] = useState('');

    const projectsQuery = useStableQuery<ProjectDocType>({});
    const { results: projectsDocs } = useLiveRxQuery<ProjectDocType>({
        collection: 'projects',
        query: projectsQuery
    });
    const projects = useMemo(
        () => projectsDocs.map((doc) => doc.toMutableJSON() as ProjectDocType),
        [projectsDocs]
    );

    const rows = useMemo<FieldCardRow[]>(
        () =>
            (projects as ProjectDocType[])
                .filter((project) => hasAnyFieldCategory(ability, project.id))
                .map((project) => {
                    const parts = projectTitleParts(project);
                    return {
                        id: project.id,
                        title: <ProjectLabel project={project} />,
                        subtitle: parts[2] || undefined,
                        highlighted: project.id === activeProjectId,
                        sortKey: projectLabelText(project),
                        searchText: parts.join(' ')
                    };
                }),
        [projects, ability, activeProjectId]
    );

    const handleSelect = useCallback((id: string) => onPick(id), [onPick]);

    return (
        <FieldCardList
            rows={rows}
            onSelect={handleSelect}
            emptyMessageId="field.projects.empty"
            noMatchesMessageId="field.projects.noMatches"
            search={search}
            onSearchChange={setSearch}
        />
    );
}
